import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import ItemColumn from "../../components/itemColumn";
import BookmarkView from "../../components/BookmarkView";
import BulkAssignDialog from "../../components/BulkAssignDialog";
import MobileRestriction from "../../components/MobileRestriction";
import FocusModeView from "../../components/FocusModeView";
import toast from "react-hot-toast";

// Zustand Store
import useCanvasStore from "../../stores/canvasStore";

// Redux - Only for user auth and footer bar (not canvas data)
import { useDispatch, useSelector } from "react-redux";
import { setFooterBarProps, resetFooterBarProps } from "../../redux/actions/global.action";
import { selectUser } from "../../redux/selectors/canvas.selectors";

// Navigation Hook
import { useCanvasNavigation } from "../../hooks/useCanvasNavigation";

const Home = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { folderId } = useParams();
    const scrollContainerRef = useRef(null);
    const accessToken = Cookies.get('access_token');

    // ============ Zustand Store State & Actions ============
    const {
        items,
        isLoading,
        selectedItems,
        selectedPath,
        archiveStatus,
        focusMode,
        bookmarkViewActive,
        setBookmarkViewActive,
        fetchCanvas,
        setArchiveStatus,
        setBulkAssignDialogOpen,
        clearSelection,
        fetchUsers,
        fetchUnreadCounts,
        setFocusMode
    } = useCanvasStore();

    // ============ Redux State (only for auth) ============
    const user = useSelector(selectUser);

    // ============ Bulk Assign Handler ============
    const handleBulkAssignShortcut = useCallback(() => {
        if (selectedItems.length < 1) {
            toast("Please select at least 1 item to use bulk assign");
            return;
        }

        const shortcuts = selectedItems.filter(item => item.item_type === 'shortcut');
        if (shortcuts.length > 0) {
            toast.error("Bulk assign not supported for shortcuts. Please select only folders/links.");
            return;
        }

        setBulkAssignDialogOpen(true);
    }, [selectedItems, setBulkAssignDialogOpen]);

    // ============ Use Navigation Hook ============
    useCanvasNavigation({
        scrollContainerRef,
        folderId,
        onBulkAssign: handleBulkAssignShortcut,
    });

    // ============ Bookmark View from URL ============
    // "/" is the bookmark view landing page; /canvas and /folder/:id show the canvas
    const isBookmarkRoute = !folderId && location.pathname !== '/canvas';

    useEffect(() => {
        setBookmarkViewActive(isBookmarkRoute);
    }, [isBookmarkRoute, setBookmarkViewActive]);

    // ============ Initialize Archive Value from URL ============
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlArchiveStatus = params.get('archiveStatus');

        if (urlArchiveStatus && urlArchiveStatus !== archiveStatus) {
            // Update store and cookie
            setArchiveStatus(urlArchiveStatus);
        }
    }, [location.search]);

    // ============ Handle Focus Mode URL Parameters (for search result navigation) ============
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlFocusMode = params.get('focusMode');
        const urlItemId = params.get('itemId');
        const urlCommentId = params.get('commentId');

        // If focusMode=true is in URL, enable focus mode and navigate to the item
        if (urlFocusMode === 'true' && urlItemId) {
            // Enable focus mode
            setFocusMode(true);

            // Navigate to the item's folder URL (this triggers FocusModeView to show the item)
            // Keep commentId in URL for FocusModeCommentsPanel to read
            const commentParam = urlCommentId ? `?commentId=${urlCommentId}` : '';
            navigate(`/folder/${urlItemId}${commentParam}`, { replace: true });
        } else if (urlFocusMode === 'false') {
            // focusMode=false opens the regular canvas view (e.g. assignment notifications from the Android app)
            setFocusMode(false);

            // Drop the param so it doesn't override focus mode toggled later on this page
            params.delete('focusMode');
            const remainingParams = params.toString();
            navigate({ search: remainingParams ? `?${remainingParams}` : '' }, { replace: true });
        }
    }, [location.search, setFocusMode, navigate]);

    // ============ Fetch Users on Mount ============
    useEffect(() => {
        if (accessToken) {
            fetchUsers();
        }
    }, [accessToken, fetchUsers]);

    // ============ Configure FooterBar Visibility ============
    useEffect(() => {
        dispatch(setFooterBarProps({
            showArchiveToggle: true,
            showSortToggle: true,
            showMinimalToggle: true
        }));

        return () => {
            dispatch(resetFooterBarProps());
        };
    }, [dispatch]);

    // ============ Fetch Canvas Data ============
    // Get currentFolder from store for smart caching
    const currentFolder = useCanvasStore(state => state.currentFolder);

    useEffect(() => {
        // The bookmark view doesn't show the canvas; keep whatever folder the canvas was last on
        if (accessToken && !isBookmarkRoute) {
            const targetFolderId = folderId ? parseInt(folderId, 10) : null;

            // Skip fetch if we already have data for this folder (returning from search/channels)
            const hasExistingData = Object.keys(items).length > 0;
            const sameFolder = currentFolder?.id === targetFolderId;

            if (hasExistingData && sameFolder) {
                return; // Skip re-fetch, use cached data
            }

            // Fetch canvas data (full path to current folder)
            // Backend returns selected_path which tells us which folders are in the current path
            fetchCanvas(targetFolderId);
        }
    }, [accessToken, folderId, isBookmarkRoute, fetchCanvas]);

    // ============ Fetch Unread Counts (async, non-blocking) ============
    // Lazy-load: Only fetch for current folder's children and ancestors in the path
    // Folder whose children's counts were already fetched while its canvas was still loading
    const earlyUnreadFetchRef = useRef(null);

    useEffect(() => {
        if (accessToken && !isLoading && Object.keys(items).length > 0) {
            const targetFolderId = folderId ? parseInt(folderId, 10) : null;

            // Right after navigation, selectedPath still belongs to the previous folder
            // until fetchCanvas returns (current_folder is null at root)
            const pathIsFresh = (currentFolder?.id ?? null) === targetFolderId;

            if (!pathIsFresh) {
                // Current folder's counts don't depend on the path, so start them now;
                // ancestor counts wait for the fresh path
                fetchUnreadCounts(targetFolderId);
                earlyUnreadFetchRef.current = targetFolderId;
                return;
            }

            // Fetch unread counts for current folder's children (skip if already started above)
            if (earlyUnreadFetchRef.current === targetFolderId) {
                earlyUnreadFetchRef.current = null;
            } else {
                fetchUnreadCounts(targetFolderId);
            }

            // If we're inside a folder (not at root), fetch counts for ancestors in the path
            if (selectedPath && selectedPath.length > 0) {
                // The first ancestor in the path is the root-level folder
                // Fetch root counts but ONLY keep the count for this specific ancestor
                const rootAncestorId = selectedPath[0];
                fetchUnreadCounts(null, [rootAncestorId]);

                // Fetch counts for each ancestor's children (so intermediate badges show correctly)
                const uniqueAncestors = [...new Set(selectedPath)];
                uniqueAncestors.forEach(ancestorId => {
                    if (ancestorId !== targetFolderId) {
                        fetchUnreadCounts(ancestorId);
                    }
                });
            }
        }
    }, [accessToken, folderId, isLoading, items, selectedPath, currentFolder, fetchUnreadCounts]);

    // ============ Auto-scroll to show children column when folder is opened ============
    const columnCount = useMemo(() => Object.keys(items).length, [items]);
    const pathDepth = selectedPath?.length || 0;

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || columnCount === 0) return;

        const align = () => {
            const frame = container.getBoundingClientRect();
            const crossing = [...container.querySelectorAll(':scope > [data-depth]')].find((column) => {
                const rect = column.getBoundingClientRect();
                return rect.left < frame.left - 1 && rect.right > frame.left + 1;
            });
            if (!crossing) return;
            const rect = crossing.getBoundingClientRect();
            container.scrollLeft += rect.right - frame.left;
        };

        const timers = [50, 200, 600].map((delay) => setTimeout(align, delay));
        const observer = new ResizeObserver(align);
        observer.observe(container);
        container.querySelectorAll(':scope > [data-depth]').forEach((column) => observer.observe(column));
        container.addEventListener('scroll', align, { passive: true });
        return () => {
            timers.forEach(clearTimeout);
            observer.disconnect();
            container.removeEventListener('scroll', align);
        };
    }, [columnCount, pathDepth, items]);

    // ============ Mobile Portrait Check ============
    const isMobilePortrait = window?.innerWidth <= 600;
    if (isMobilePortrait) {
        return <MobileRestriction />;
    }

    // ============ Loading State ============
    if (isLoading && Object.keys(items).length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-muted-foreground">Loading...</div>
            </div>
        );
    }

    // ============ Render ============
    return (
        user?.username ? (
            <div className="flex flex-col h-full min-w-0 bg-background overflow-hidden">
                {/* Focus Mode View */}
                {focusMode ? (
                    <FocusModeView />
                ) : bookmarkViewActive ? (
                    /* Bookmark View - Bookmarked folders, assigned folders and @mentions */
                    <div className="flex-1 min-h-0 min-w-0 flex flex-col">
                        <BookmarkView />
                    </div>
                ) : (
                    /* Normal Canvas View */
                    <div
                        ref={scrollContainerRef}
                        className="canvas-container min-w-0 w-full max-w-full flex-1 flex overflow-x-auto overflow-y-hidden"
                    >
                        {Object.keys(items).map((depthKey) => (
                            <div
                                key={depthKey}
                                className="item-column shrink-0"
                                data-depth={depthKey}
                            >
                                <ItemColumn depth={parseInt(depthKey, 10)} />
                            </div>
                        ))}
                    </div>
                )}

                {/* BulkAssignDialog now reads all data from Zustand store */}
                <BulkAssignDialog />
            </div>
        ) : <div className="flex items-center justify-center h-full text-muted-foreground">Not Logged in</div>
    );
};

export default Home;
